/**
 * AuthContext - React Context for Authentication State Management
 *
 * Provides authentication state and methods throughout the application.
 *
 * Features:
 * - User session management
 * - Login/logout functionality
 * - Permission checking
 * - localStorage persistence
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { login as serverLogin, clearSession, getSessionToken } from '../services/uploadClient';

const AuthContext = createContext(undefined);

/**
 * Hook to access authentication context
 * Must be used within an AuthProvider
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * AuthProvider Component
 *
 * Wraps the application to provide authentication state
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check for stored session on mount
  useEffect(() => {
    const storedUser = localStorage.getItem('evidencer_user');
    // The server-issued session token expires; without a live one the stored profile is stale.
    if (storedUser && !getSessionToken()) {
      localStorage.removeItem('evidencer_user');
    } else if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        setIsAuthenticated(true);
        console.log('✅ Session restored for user:', parsedUser.username);
      } catch (error) {
        console.error('Error parsing stored user:', error);
        localStorage.removeItem('evidencer_user');
      }
    }
    setIsLoading(false);
  }, []);

  /**
   * Login user with username and password
   *
   * @param {string} username - Username
   * @param {string} password - Password
   * @returns {Promise<boolean>} - Success status
   */
  const login = async (username, password) => {
    try {
      // Credentials are verified server-side (/api/login); the browser only receives a
      // signed, expiring session token plus a profile without passcode or API key.
      const response = await serverLogin(username, password);

      if (response.success && response.user) {
        setUser(response.user);
        setIsAuthenticated(true);
        localStorage.setItem('evidencer_user', JSON.stringify(response.user));
        console.log('✅ User logged in:', username);
        return true;
      }

      console.warn('⚠️  Login failed:', response.error);
      return false;
    } catch (error) {
      console.error('❌ Login error:', error);
      return false;
    }
  };

  /**
   * Logout current user
   */
  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('evidencer_user');
    clearSession();
    console.log('👋 User logged out');
  };

  /**
   * Check if user has upload permission
   *
   * @returns {boolean} - Whether user can upload
   */
  const canUploadEvidence = () => {
    if (!isAuthenticated || !user) {
      return false;
    }

    // Admin can always upload
    if (user.userRole === 'Admin') {
      return true;
    }

    // Check specific upload permission
    return user.canUpload === true;
  };

  /**
   * Check if user has delete permission
   *
   * @returns {boolean} - Whether user can delete
   */
  const canDeleteEvidence = () => {
    if (!isAuthenticated || !user) {
      return false;
    }

    // Admin can always delete
    if (user.userRole === 'Admin') {
      return true;
    }

    // Check specific delete permission
    return user.canDelete === true;
  };

  /**
   * Check if user has permission to access specific evidence
   *
   * @param {string} accessLevel - Required access level (Public, Authenticated, Private, Admin Only)
   * @returns {boolean} - Whether user has access
   */
  const checkAccess = (accessLevel) => {
    // Public files are always accessible
    if (accessLevel === 'Public') {
      return true;
    }

    // Not authenticated - no access to protected content
    if (!isAuthenticated || !user) {
      return false;
    }

    // Admin can access everything
    if (user.userRole === 'Admin') {
      return true;
    }

    // Admin Only files require Admin role
    if (accessLevel === 'Admin Only') {
      return false;
    }

    // Private level - Content Managers can access
    if (accessLevel === 'Private') {
      return user.userRole === 'Content Manager';
    }

    // Authenticated level - any authenticated user
    if (accessLevel === 'Authenticated') {
      return true;
    }

    return false;
  };

  /**
   * Get user's maximum upload size limit
   *
   * @returns {number} - Maximum upload size in MB
   */
  const getMaxUploadSize = () => {
    if (!user) {
      return 0;
    }
    return user.maxUploadSizeMB || 100;
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    canUploadEvidence,
    canDeleteEvidence,
    checkAccess,
    getMaxUploadSize
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
